import { Button, Card, Elevation, Spinner } from '@blueprintjs/core';
import useComponentSize from '@rehooks/component-size';
import { FGMaskInput, FGSelectInput, FGTextInput, useApiEffect } from 'nsitools-react';
import * as React from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';

import { BreedApi, BreedTranslationDto, ClubApi } from '../../../api';
import assets from '../../../assets';
import { FGCountrySelect, FGMultiTranslate } from '../../../components';
import { useGlobalData } from '../../../contexts';
import { useApiService, useTl } from '../../../hooks';
import { useReferential } from '../../../hooks/useReferential';
import { ETLCodes } from '../../../locales';
import { DrupalClubCard } from '../../common/DrupalClubCard';
import { DrupalFormGenerator } from '../../common/DrupalFormGenerator';

export interface IDrupalBreedDetailPageProps {}

const BreedMainInformationContainer = styled(Card)`
  margin: 2rem;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
`;

const SpaceContainer = styled.div`
  height: 3rem;
`;

const AboutContainer = styled.div`
  background-color: #ccd2f0;
  margin: 3rem;
`;

export const DrupalBreedDetailPage: React.FunctionComponent<IDrupalBreedDetailPageProps> = () => {
  const api = useApiService(BreedApi);
  const clubApi = useApiService(ClubApi);
  const { t, tUnsafe } = useTl();
  const { id } = useParams<{ id: string }>();
  const { currentLanguage } = useGlobalData();

  const [breed, loading] = useApiEffect(() => api.get(+id));
  const [clubs, cloading] = useApiEffect(() => clubApi.getClubsByBreed(+id, currentLanguage.languageId));

  const [breedGroups, breedGroupsLoading] = useReferential((c) => c.getBreedGroups(), true, []);
  const [agility, agilityLoading] = useReferential((a) => a.getAgility(), false);
  const [grooming, groomingLoading] = useReferential((a) => a.getGrooming(), false);
  const [translatedAgility, setTranslatedAgilities] = React.useState([]);
  const [translatedGrooming, setTranslatedGroomings] = React.useState([]);

  React.useEffect(() => {
    if (!agilityLoading && agility) {
      const newAgility = agility.map((c) => {
        return { label: tUnsafe(c.label), value: c.value };
      });
      setTranslatedAgilities(newAgility);
    }
  }, [agility, agilityLoading, tUnsafe]);

  React.useEffect(() => {
    if (!groomingLoading && grooming) {
      const newGrooming = grooming.map((c) => {
        return { label: tUnsafe(c.label), value: c.value };
      });
      setTranslatedGroomings(newGrooming);
    }
  }, [grooming, groomingLoading, tUnsafe]);

  const formRef = React.useRef<HTMLDivElement>(null);
  let formDimensions = useComponentSize(formRef);

  return loading || cloading ? (
    <Spinner />
  ) : (
    <>
      <h1 style={{ textAlign: "center" }}>{breed.officialName}</h1>
      <BreedMainInformationContainer elevation={Elevation.FOUR}>
        <div ref={formRef}>
          <DrupalFormGenerator
            initialValues={breed}
            onSubmit={() => {}}
            editMode={true}
            showDeleteButton={false}
            hideButtons={true}
            saving={false}
            deleting={false}
          >
            <div style={{ marginBottom: "2rem" }}>
              <FGMultiTranslate
                label={ETLCodes.Name}
                translationField="text"
                translationsArrayField="breedTranslations"
                translationDto={BreedTranslationDto}
                readonly
              />
              <FGTextInput label={t(ETLCodes.OfficialName)} name="officialName" readonly />
            </div>

            <FGSelectInput
              name="breedGroupId"
              label={t(ETLCodes.BreedGroupLabel)}
              items={breedGroups}
              loading={breedGroupsLoading}
              readonly
            />
            <FGSelectInput
              name="grooming"
              label={t(ETLCodes.Grooming)}
              items={translatedGrooming}
              loading={groomingLoading}
              readonly
            />
            <FGSelectInput
              name="agility"
              label={t(ETLCodes.Agility)}
              items={translatedAgility}
              loading={agilityLoading}
              readonly
            />
            <FGMaskInput
              cleaveOptions={{
                numericOnly: true,
                numeral: true,
                numeralPositiveOnly: true,
              }}
              name="sizeMin"
              label={t(ETLCodes.SizeFrom) + ` (cm)`}
              readonly
            />
            <FGMaskInput
              cleaveOptions={{
                numericOnly: true,
                numeral: true,
                numeralPositiveOnly: true,
              }}
              name="sizeMax"
              label={t(ETLCodes.SizeTo) + ` (cm)`}
              readonly
            />
            <FGCountrySelect name="countryId" label={t(ETLCodes.Country)} readonly />
          </DrupalFormGenerator>
        </div>
        <img
          alt={breed.officialName}
          src={assets.images.dogDefault}
          style={{
            maxHeight: formDimensions.height !== null ? formDimensions.height : "400px",
            maxWidth: formDimensions.width !== null ? formDimensions.width : "600px",
          }}
        />
      </BreedMainInformationContainer>
      <SpaceContainer />
      <h1 style={{ textAlign: "center" }}>{t(ETLCodes.About)}</h1>
      <AboutContainer>
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <div style={{ margin: "2rem" }}>
            <h3>{t(ETLCodes.WhatToKnow)}</h3>
            <p>{t(ETLCodes.BreedInformationText)}</p>
          </div>
          <Button text={t(ETLCodes.DownloadBreedDocument)} icon="document" minimal style={{ color: "#0091ff" }} />
        </div>
      </AboutContainer>

      <SpaceContainer />
      <h1 style={{ textAlign: "center" }}>{t(ETLCodes.Clubs)}</h1>
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-evenly" }}>
        {clubs.map((item, key) => (
          <DrupalClubCard key={key} clubInformation={item} />
        ))}
      </div>
    </>
  );
};
